import React from "react";
import PageLayout from "@/components/Layout/PageLayout";
import { HeroBanner } from "@/components/ui/hero-banner";
import { FeatureSection } from "@/components/ui/feature-section";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Hospital, UserCircle, Building, HeartPulse, UserCog, Shield } from "lucide-react";
import { UserDetails } from "@/components/auth/UserDetails";

const HomePage: React.FC = () => {
  return (
    <PageLayout>
      <HeroBanner
        title="Welcome to the Health Portal"
        description="Connecting healthcare providers, patients, and insurance companies for better healthcare management."
        backgroundColor="bg-gradient-to-r from-blue-600 to-blue-800"
      />

      {/* Debug Info (optional) */}
      {/* <section className="py-8 bg-white">
        <div className="container px-4 md:px-6 mx-auto">
          <h2 className="text-2xl font-bold text-center mb-6">
            Auth Debug Information
          </h2>
          <UserDetails />
        </div>
      </section> */}

      <section className="py-8 md:py-12 lg:py-16 bg-white">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="text-center space-y-4">
            <h2 className="text-2xl md:text-4xl font-bold">Choose Your Portal</h2>
            <p className="text-gray-500 max-w-[900px] mx-auto text-sm md:text-base">
              Select the portal that matches your role to access the appropriate tools and information.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            {[
              { to: "/doctor", label: "For Doctors", icon: <UserCircle />, desc: "Access patient records, manage appointments, and coordinate care." },
              { to: "/hospital", label: "For Hospitals", icon: <Hospital />, desc: "Manage facility resources, staff, and operational efficiency." },
              { to: "/patient", label: "For Patients", icon: <HeartPulse />, desc: "View your health records, schedule appointments, and manage medications." },
              { to: "/insurance", label: "For Insurance", icon: <Building />, desc: "Process claims, verify coverage, and manage provider networks." },
              { to: "/staff", label: "For Staff", icon: <UserCog />, desc: "Manage staff resources, schedules, and performance." },
              { to: "/admin", label: "For Admin", icon: <Shield />, desc: "Manage system settings, user roles, and access controls." }
            ].map(({ to, label, icon, desc }) => (
              <Link to={to} key={to} className="block h-full">
                <div className="flex flex-col items-center h-full p-6 border rounded-lg shadow-sm hover:shadow-md transition">
                  <div className="p-3 rounded-full bg-blue-100 text-blue-600 mb-4">
                    {React.cloneElement(icon, { size: 24, className: "md:w-8 md:h-8" })}
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">{label}</h3>
                  <p className="text-gray-500 text-sm text-center mb-4">{desc}</p>
                  <Button variant="outline" className="mt-auto">Enter Portal</Button>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FeatureSection 
        title="Why Choose Our Health Portal"
        description="Our platform offers a range of features designed to streamline healthcare management for all stakeholders."
        features={[
          {
            title: "Centralized Records",
            description: "All health records in one secure, accessible location for authorized users.",
            icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/><path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3"/></svg>
          },
          {
            title: "Seamless Communication",
            description: "Direct messaging between providers, patients, and insurance companies.",
            icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          },
          {
            title: "Appointment Management",
            description: "Schedule, track, and manage appointments with real-time updates.",
            icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>
          },
          {
            title: "Secure Access",
            description: "Role-based security ensuring information is available only to authorized users.",
            icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/></svg>
          },
          {
            title: "Analytics Dashboard",
            description: "Visual metrics and reports for healthcare insights and operational improvements.",
            icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" x2="12" y1="20" y2="10"/><line x1="18" x2="18" y1="20" y2="4"/><line x1="6" x2="6" y1="20" y2="16"/></svg>
          },
          {
            title: "Mobile Friendly",
            description: "Access your portal anytime, anywhere from any device with internet connection.",
            icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="20" x="5" y="2" rx="2" ry="2"/><path d="M12 18h.01"/></svg>
          }
        ]}
      />
    </PageLayout>
  );
};

export default HomePage;
